import { Button } from 'primereact/button';
import { motion } from 'framer-motion';

import './landing.scss';

const FEATURES = [
  // {
  //   icon: 'pi-calendar',
  //   title: 'Удобное расписание',
  //   text: 'Планируйте занятия в пару кликов и отправляйте приглашения ученикам ссылкой — без регистрации с их стороны.'
  // },
  {
    icon: 'pi-pencil',
    title: 'Интерактивные доски',
    text: 'Рисуйте, решайте задачи и объясняйте материал на живой доске вместе с учеником в реальном времени.'
  },
  {
    icon: 'pi-file',
    title: 'Совместная работа с документами',
    text: 'Открывайте PDF и материалы прямо во встрече — каждый видит одно и то же и может оставлять пометки.'
  },
  {
    icon: 'pi-microphone',
    title: 'Живое общение',
    text: 'Стабильная связь с шумоподавлением — вас слышно даже на слабом интернете.'
  },
  {
    icon: 'pi-users',
    title: 'Группы и комнаты',
    text: 'Проводите групповые занятия, делите учеников по комнатам для самостоятельной работы и парных задач.'
  },
  {
    icon: 'pi-bolt',
    title: 'Два режима — теория и практика',
    text: 'В режиме теории ученики видят ваши файлы и доску, в режиме практики — у каждого свое рабочее пространство.'
  },
  {
    icon: 'pi-save',
    title: 'Результаты встречи',
    text: 'После окончания встречи ученики получают копии материалов и своей работы на интерактивной доске.'
  }
];

const STEPS = [
  {
    n: '01',
    title: 'Создайте встречу',
    text: 'Введите имя, нажмите «Создать» — мгновенно получаете ссылку на занятие.'
  },
  {
    n: '02',
    title: 'Пригласите ученика',
    text: 'Отправьте ссылку в любом мессенджере. Никаких аккаунтов и установок не нужно.'
  },
  {
    n: '03',
    title: 'Ведите занятие',
    text: 'Видео, доска, документы и чат — всё в одном окне. После занятия материалы остаются у вас.'
  }
];

const FAQ = [
  {
    q: 'Repeteka действительно бесплатна?',
    a: 'Да. Все возможности — связь, интерактивная доска, работа с документами и групповые комнаты — доступны бесплатно, без скрытых платежей и ограничений по длительности занятия.'
  },
  {
    q: 'Нужен ли ученику аккаунт, чтобы присоединиться к занятию?',
    a: 'Нет. Вы отправляете ученику ссылку, он открывает её в браузере и сразу попадает на занятие — без регистрации, входа и установки приложений.'
  },
  {
    q: 'Нужно ли что-то устанавливать?',
    a: 'Нет. Repeteka работает прямо в браузере на компьютере, планшете и телефоне — скачивать и настраивать ничего не нужно ни вам, ни ученику.'
  },
  {
    q: 'Можно ли проводить групповые занятия?',
    a: 'Да. Вы можете вести занятия с несколькими учениками и делить их по отдельным комнатам для самостоятельной или парной работы.'
  },
  {
    q: 'Сохраняются ли материалы после занятия?',
    a: 'Да. После окончания встречи материалы и работа на интерактивной доске сохраняются — вы и ученики получаете копии, чтобы вернуться к ним позже.'
  },
  {
    q: 'Будет ли связь работать на слабом интернете?',
    a: 'Да. Мы используем шумоподавление и оптимизацию соединения, поэтому вас хорошо слышно даже при нестабильном интернете.'
  }
];

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.2 },
  transition: { duration: 0.5, ease: 'easeOut' as const }
};

function Landing() {
  const goToApp = () => {
    if (typeof window !== 'undefined') {
      window.location.href = '/';
    }
  };

  const scrollTo = (id: string) => () => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="landing">
      <section className="landing__hero">
        <div className="landing__hero-inner">
          <motion.div
            className="landing__hero-copy"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: 'easeOut' as const }}
          >
            <span className="landing__eyebrow">Платформа для репетиторов</span>
            <h1 className="landing__title">
              Онлайн-занятия, <span className="landing__title-accent">которые удобно вести</span>
            </h1>
            <p className="landing__subtitle">
              Repeteka — рабочее место преподавателя: общение, интерактивная доска, документы и
              группы учеников в одном окне. Без установок и регистрации со стороны ученика.
            </p>
            <div className="landing__cta-row">
              <Button
                className="landing__cta"
                size="large"
                onClick={goToApp}
                label="Начать занятие"
                icon="pi pi-arrow-right"
                iconPos="right"
              />
              <Button
                className="landing__cta-secondary"
                size="large"
                outlined
                onClick={scrollTo('features')}
                label="Возможности"
              />
            </div>
            <ul className="landing__hero-bullets">
              <li>
                <i className="pi pi-check" /> Полностью бесплатный сервис
              </li>
              <li>
                <i className="pi pi-check" /> Аккаунт не обязателен
              </li>
              <li>
                <i className="pi pi-check" /> Работает в браузере
              </li>
            </ul>
          </motion.div>

          <motion.div
            className="landing__hero-visual"
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, ease: 'easeOut' as const, delay: 0.1 }}
          >
            <div className="landing__mock">
              <div className="landing__mock-bar">
                <span />
                <span />
                <span />
              </div>
              <div className="landing__mock-body">
                <div className="landing__mock-board">
                  <div className="landing__mock-equation">
                    <span>
                      x<sup>2</sup> + 5x − 14 = 0
                    </span>
                    <span className="landing__mock-equation-arrow">→</span>
                    <span>(x − 2)(x + 7) = 0</span>
                  </div>
                  <div className="landing__mock-graph">
                    <svg viewBox="0 0 200 100" preserveAspectRatio="none">
                      <path
                        d="M0,90 Q50,10 100,50 T200,15"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                      />
                    </svg>
                  </div>
                </div>
                <div className="landing__mock-tiles">
                  <div className="landing__mock-tile landing__mock-tile_self">
                    <i className="pi pi-user" />
                    <span>Анна (вы)</span>
                  </div>
                  <div className="landing__mock-tile">
                    <i className="pi pi-user" />
                    <span>Миша</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="landing__hero-blob landing__hero-blob_a" />
            <div className="landing__hero-blob landing__hero-blob_b" />
          </motion.div>
        </div>
      </section>

      <section id="features" className="landing__features">
        <motion.div className="landing__section-head" {...fadeUp}>
          <h2 className="landing__section-title">Всё для онлайн-занятия в одном окне</h2>
          <p className="landing__section-sub">
            Без зоопарка вкладок и сторонних сервисов — Repeteka заменяет аудиозвонок, доску и
            редактор документов.
          </p>
        </motion.div>

        <div className="landing__feature-grid">
          {FEATURES.map((f, i) => (
            <motion.div
              key={f.title}
              className="landing__feature"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.45, ease: 'easeOut' as const, delay: i * 0.06 }}
            >
              <div className="landing__feature-icon">
                <i className={`pi ${f.icon}`} />
              </div>
              <h3 className="landing__feature-title">{f.title}</h3>
              <p className="landing__feature-text">{f.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section id="how" className="landing__how">
        <motion.div className="landing__section-head" {...fadeUp}>
          <h2 className="landing__section-title">Как это работает</h2>
          <p className="landing__section-sub">
            Первое занятие можно начать прямо сейчас — за 30 секунд.
          </p>
        </motion.div>

        <div className="landing__steps">
          {STEPS.map((s, i) => (
            <motion.div
              key={s.n}
              className="landing__step"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.45, ease: 'easeOut', delay: i * 0.08 }}
            >
              <div className="landing__step-num">{s.n}</div>
              <h3 className="landing__step-title">{s.title}</h3>
              <p className="landing__step-text">{s.text}</p>
            </motion.div>
          ))}
        </div>

        <motion.div className="landing__how-cta" {...fadeUp}>
          <Button
            size="large"
            onClick={goToApp}
            label="Создать встречу бесплатно"
            icon="pi pi-arrow-right"
            iconPos="right"
            className="landing__cta"
          />
        </motion.div>
      </section>

      <section id="faq" className="landing__faq">
        <motion.div className="landing__section-head" {...fadeUp}>
          <h2 className="landing__section-title">Частые вопросы</h2>
          <p className="landing__section-sub">
            Коротко о главном — остальное всегда подскажет поддержка.
          </p>
        </motion.div>

        <div className="landing__faq-list">
          {FAQ.map((item, i) => (
            <motion.details
              key={item.q}
              className="landing__faq-item"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.4, ease: 'easeOut' as const, delay: i * 0.05 }}
            >
              <summary className="landing__faq-q">
                {item.q}
                <i className="pi pi-chevron-down" />
              </summary>
              <p className="landing__faq-a">{item.a}</p>
            </motion.details>
          ))}
        </div>
      </section>

      <section className="landing__final">
        <motion.div className="landing__final-card" {...fadeUp}>
          <h2 className="landing__final-title">Проводите занятия так, как вам удобно</h2>
          <p className="landing__final-sub">
            Repeteka — это рабочее место, которое не мешает преподавать. Откройте ссылку, и через
            минуту вы уже на занятии.
          </p>
          <Button
            size="large"
            onClick={goToApp}
            label="Начать сейчас"
            icon="pi pi-arrow-right"
            iconPos="right"
            className="landing__cta"
          />
        </motion.div>
      </section>

      <footer className="landing__footer">
        <div className="landing__footer-inner">
          <div className="landing__footer-brand">
            <img src="/logo.png" alt="Repeteka" />
            <span>Repeteka</span>
          </div>
          <nav className="landing__footer-nav">
            <a href="/">Создать встречу</a>
            <a
              href="#features"
              onClick={(e) => {
                e.preventDefault();
                scrollTo('features')();
              }}
            >
              Возможности
            </a>
            <a
              href="#how"
              onClick={(e) => {
                e.preventDefault();
                scrollTo('how')();
              }}
            >
              Как это работает
            </a>
            <a
              href="#faq"
              onClick={(e) => {
                e.preventDefault();
                scrollTo('faq')();
              }}
            >
              Вопросы
            </a>
            <a href="https://t.me/repeteka_bot" target="_blank" rel="noreferrer">
              Telegram-бот
            </a>
            <a href="https://t.me/repeteka_support" target="_blank" rel="noreferrer">
              Поддержка
            </a>
          </nav>
          <div className="landing__footer-meta">© {new Date().getFullYear()} Repeteka</div>
        </div>
      </footer>
    </div>
  );
}

export default Landing;
